import { FormEvent, useEffect, useState } from 'react';
import type { CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import { ErrorBox, Loading } from '../components/Feedback';
import { ModuleHeader } from '../components/ModuleHeader';
import { Pager } from '../components/Pager';
import { api } from '../lib/api';
import { chanceColor, normalizeChance } from '../lib/chance';
import { useAuth } from '../lib/auth';
import type { Lead } from '../types';

interface LeadFilters {
  search: string;
  chanceThreshold?: number;
  source: string;
  assignedUser: string;
}

function rows(payload: any): Lead[] { return Array.isArray(payload) ? payload : payload?.items || payload?.rows || []; }
function responsible(lead: Lead): string { return lead.assignedUser?.name || lead.assignedUser?.email || lead.asignatLa || 'Nealocat'; }
function chanceStyle(value: number): CSSProperties { return { '--chance-color': chanceColor(value) } as CSSProperties; }
function leadListName(lead: Lead): string {
  return String(lead.firma || '').trim() || String(lead.nume || '').trim() || 'Lead fără nume';
}

export function LeadsPage() {
  const { can } = useAuth();
  const [items, setItems] = useState<Lead[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [chanceThreshold, setChanceThreshold] = useState(0);
  const [source, setSource] = useState('');
  const [assignedUser, setAssignedUser] = useState('');
  const [filters, setFilters] = useState<LeadFilters>({ search: '', source: '', assignedUser: '' });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoading(true);
    setError('');
    if (filters.chanceThreshold != null && filters.chanceThreshold >= 100) {
      setItems([]);
      setTotal(0);
      setLoading(false);
      return;
    }
    const { chanceThreshold: appliedThreshold, ...requestFilters } = filters;
    void api.leads({ page, limit: 25, ...requestFilters, chanceMin: appliedThreshold == null ? undefined : appliedThreshold + 1 }).then((result) => {
      setItems(rows(result));
      setTotal(Number(result?.total ?? rows(result).length));
    }).catch((reason) => setError(reason instanceof Error ? reason.message : 'Lead-urile nu pot fi încărcate.')).finally(() => setLoading(false));
  }, [filters, page]);

  function submit(event: FormEvent) {
    event.preventDefault();
    setPage(1);
    setFilters({
      search: search.trim(),
      chanceThreshold,
      source,
      assignedUser: assignedUser.trim(),
    });
  }

  return <div className="page"><ModuleHeader title="Leads" subtitle={`${total} înregistrări`} /><main className="page-body module-body">
    <form className="card filter-card" onSubmit={submit}>
      <label className="search-field"><span>Caută lead</span><input className="input" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Nume, firmă, CUI, telefon, email…" /></label>
      <div className="filter-grid"><input className="input" value={source} onChange={(event) => setSource(event.target.value)} placeholder="Sursă" /><input className="input" value={assignedUser} onChange={(event) => setAssignedUser(event.target.value)} placeholder="Responsabil" /></div>
      <fieldset className="chance-filter"><legend>Filtru Șansă</legend><div className="chance-filter__values"><span>Lead-uri cu Șansă peste <strong>{chanceThreshold}%</strong></span></div><label className="chance-filter__threshold" style={chanceStyle(chanceThreshold)}><span>Prag Șansă</span><input className="chance-range" type="range" min="0" max="100" step="1" value={chanceThreshold} aria-label="Prag pentru Șansă" aria-valuetext={`Peste ${chanceThreshold}%`} onChange={(event) => setChanceThreshold(normalizeChance(event.currentTarget.value))} /></label></fieldset>
      <button className="btn btn-secondary block">Aplică filtrele</button>
    </form>
    {can('leads', 'write') && <Link className="btn btn-primary block" to="/leads/new">+ Lead nou</Link>}
    {loading && <Loading />}{error && <ErrorBox message={error} />}
    {!loading && !items.length && <div className="empty"><strong>Niciun lead</strong><span>Nu există rezultate pentru filtrele selectate.</span></div>}
    <section className="entity-list">{items.map((lead) => {
      const chance = normalizeChance(lead.chance);
      return <Link className="entity-card" key={lead._id || lead.id} to={`/leads/${encodeURIComponent(lead._id || lead.id || '')}`}><div className="entity-card__top"><span className="chance-badge" style={chanceStyle(chance)}>Șansă {chance}%</span>{lead.nr && <small>#{lead.nr}</small>}</div><h2>{leadListName(lead)}</h2><div className="entity-meta"><span>{lead.localitate || lead.telefon || lead.email || '—'}</span><span>{responsible(lead)}</span></div></Link>;
    })}</section>
    <Pager page={page} total={total} limit={25} onChange={setPage} />
  </main></div>;
}
